import { apiBaseUrl } from './env';
import { getAccessToken, refreshSession, setAccessToken } from './session';

/**
 * Typed client for the BULK3 API, mirroring the server's response
 * envelope (spec §51).
 *
 * Deliberately a separate copy from the storefront's client: the two apps are
 * independent deployables, and the admin client will diverge as it gains
 * token-refresh and permission handling.
 */

export interface ApiFieldError {
  field: string;
  message: string;
  code?: string;
}

interface ApiSuccessEnvelope<T> {
  success: true;
  data: T;
  message: string;
}

interface ApiErrorEnvelope {
  success: false;
  message: string;
  code: string;
  errors: ApiFieldError[];
  requestId?: string;
  timestamp: string;
  path: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly fieldErrors: ApiFieldError[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Session expired or missing — the caller should redirect to login. */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /** Authenticated but lacking the required permission (spec §58). */
  get isPermissionError(): boolean {
    return this.status === 403;
  }
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Skip the bearer header — only `/auth/login` and `/auth/refresh` need this. */
  anonymous?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return url.toString();
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { body, query, headers, anonymous, ...rest } = options;
  const token = anonymous ? null : getAccessToken();

  return fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    // Carries the HttpOnly refresh-token cookie.
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options);

  // Access tokens are short-lived by design. One silent refresh-and-retry keeps
  // that from surfacing as a spurious sign-out mid-session; if the retry also
  // fails, the session really is over and the error propagates.
  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshSession();

    if (refreshed) {
      response = await send(path, options);
    } else {
      setAccessToken(null);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError(
      `The server returned a malformed response (HTTP ${response.status})`,
      response.status,
      'INVALID_RESPONSE',
    );
  }

  const envelope = payload as ApiSuccessEnvelope<T> | ApiErrorEnvelope;

  if (!response.ok || envelope.success === false) {
    const error = envelope as ApiErrorEnvelope;
    throw new ApiError(
      error.message ?? 'Request failed',
      response.status,
      error.code ?? 'UNKNOWN_ERROR',
      error.errors ?? [],
      error.requestId,
    );
  }

  return envelope.data;
}

/**
 * Fetch a binary response (KYC documents, exports).
 *
 * Kept separate from `request` because those endpoints return raw bytes rather
 * than the JSON envelope, and because a `<a href>` cannot carry the bearer
 * header these routes require.
 */
async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  let response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshSession();

    if (refreshed) {
      response = await send(path, options);
    } else {
      setAccessToken(null);
    }
  }

  if (!response.ok) {
    throw new ApiError(
      `Download failed (HTTP ${response.status})`,
      response.status,
      'DOWNLOAD_FAILED',
    );
  }

  return response.blob();
}

/**
 * Send multipart form data (product images, CSV imports).
 *
 * `Content-Type` is deliberately omitted: the browser has to set it itself so
 * it can append the multipart boundary. Setting it by hand produces a body the
 * server cannot parse.
 */
async function requestUpload<T>(
  path: string,
  form: FormData,
  options: RequestOptions = {},
): Promise<T> {
  const send = (): Promise<Response> =>
    fetch(buildUrl(path, options.query), {
      method: options.method ?? 'POST',
      headers: {
        ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
      },
      credentials: 'include',
      body: form,
    });

  let response = await send();

  if (response.status === 401) {
    const refreshed = await refreshSession();

    if (refreshed) {
      response = await send();
    } else {
      setAccessToken(null);
    }
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError(
      `The server returned a malformed response (HTTP ${response.status})`,
      response.status,
      'INVALID_RESPONSE',
    );
  }

  const envelope = payload as ApiSuccessEnvelope<T> | ApiErrorEnvelope;

  if (!response.ok || envelope.success === false) {
    const error = envelope as ApiErrorEnvelope;
    throw new ApiError(
      error.message ?? 'Upload failed',
      response.status,
      error.code ?? 'UNKNOWN_ERROR',
      error.errors ?? [],
      error.requestId,
    );
  }

  return envelope.data;
}

/**
 * Save a bearer-authenticated response to a file.
 *
 * Exports need the Authorization header, which a plain `<a href download>`
 * cannot carry, so the bytes are fetched and handed to the browser as an
 * object URL.
 */
export async function downloadFile(
  path: string,
  fileName: string,
  options?: RequestOptions,
): Promise<void> {
  const blob = await requestBlob(path, options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();

  // Give the download time to start before releasing the URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const apiClient = {
  blob: requestBlob,
  upload: requestUpload,

  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
